import { useContext, useEffect, useMemo } from "react";
import { useLazyQuery, useQuery } from "@apollo/client";
import { useParams } from "react-router-dom";
import { IParams } from "@models/IParams";
import { Query, Section } from "@models/ISchema";
import { ContextApp } from "@config/Context/contextApp";
import {
  isLinkValidForProgramming,
  ProgrammingGroupState,
  resolveProgrammingGroupState,
} from "@utils/programmingGroupState";
import linkProgrammingGateQuery from "@services/collections/programming-edit/graphql/linkProgrammingGate.query.graphql";
import { BLOCKING_THRESHOLD_QUERY } from "../../EditSessions/graphql/blockingThreshold.query";
import {
  deriveProgrammingLockState,
  PROGRAMMING_TAB_TOOLTIP_KEYS,
} from "../../EditSessions/utils/programmingLockState";
import { isLinkStarted } from "../../EditSessions/utils/isLinkStarted";

interface LinkProgrammingGate {
  isLoading: boolean;
  /** The link has microprogramming applied. */
  hasAppliedStructure: boolean;
  /** State for the modal pill. `null` when the group is outside the flow. */
  programmingState: ProgrammingGroupState | null;
  /** The "Editar asignación" action is locked, exactly as the editor tab is. */
  isAssignationLocked: boolean;
  /** Whether the programming action exists at all for this user and course. */
  canEditProgramming: boolean;
  /** The programming action exists but cannot be used right now. */
  isProgrammingDisabled: boolean;
  /** i18n key for the programming action's tooltip, or `null` when enabled. */
  programmingTooltipKey: string | null;
}

const EMPTY_GATE: LinkProgrammingGate = {
  isLoading: false,
  hasAppliedStructure: false,
  programmingState: null,
  isAssignationLocked: false,
  canEditProgramming: false,
  isProgrammingDisabled: false,
  programmingTooltipKey: null,
};

/**
 * Reproduces the sessions-editor tab gates for a single link, so the event
 * details modal can offer the same actions under the same rules.
 *
 * The calendar's own data cannot answer this: its sections belong to whatever
 * dimension is being viewed (an instructor's calendar spans many links), so
 * neither "has this link started" nor "does this link have unscheduled
 * sessions" is derivable from it. This fetches a minimal projection of the link
 * instead — three facts and one time/date pair per session — rather than
 * reusing the editor's full `CalendarSections` fragment.
 *
 * The query is lazy and only fires once a link is known, so opening the modal
 * on an event without links costs nothing. Apollo caches per link id, so
 * reopening the same event is free.
 */
export const useLinkProgrammingGate = (linkId?: string | null): LinkProgrammingGate => {
  const { origin: originId, scenario: scenarioId }: IParams = useParams();
  const { user } = useContext(ContextApp);

  const [fetchGate, { data, loading }] = useLazyQuery<Query>(linkProgrammingGateQuery, {
    fetchPolicy: "cache-first",
  });

  const { data: thresholdData } = useQuery<Query>(BLOCKING_THRESHOLD_QUERY, {
    variables: { scenarioId },
    skip: !scenarioId,
    fetchPolicy: "cache-first",
  });

  useEffect(() => {
    if (!linkId || !scenarioId || !originId) return;

    fetchGate({ variables: { scenarioId, originId, resourceId: linkId } });
  }, [linkId, scenarioId, originId]);

  return useMemo(() => {
    if (!linkId) return EMPTY_GATE;

    const editorView = (data as any)?.cube?.editorView;
    const link = editorView?.info?.link;

    // While the projection is in flight there is nothing to assert yet. Reporting
    // the loading flag lets the caller keep both actions inert rather than
    // briefly offering an action the gate may be about to withdraw.
    if (!link) return { ...EMPTY_GATE, isLoading: loading };

    const sections: Section[] = editorView?.sections ?? [];
    const blockingThreshold = (thresholdData as any)?.data?.blockingThreshold?.current_datetime;

    const hasAppliedStructure = !!link?.programmingStructure;

    // The pill reads the persisted group labels, exactly like the group header,
    // so a link whose course is not microprogrammable shows no pill at all
    // rather than a misleading "Programación pendiente".
    const groupLabels = (link?.groups ?? []).flatMap((group: any) => group?.labels ?? []);
    const isStarted = isLinkStarted(sections, blockingThreshold);
    const hasUnscheduledSessions = sections.some(section => section?.unasignedSessions?.length > 0);

    const { isAssignationLocked, programmingTabBlockReason, canEditProgramming } =
      deriveProgrammingLockState({
        hasAppliedStructure,
        isStarted,
        hasUnscheduledSessions,
        canEditStartedLinks: !!user?.abilities?.can_edit_programming_for_started_links,
        hasProgrammingAbility: !!user?.abilities?.can_edit_programming,
        isEligibleForProgramming: isLinkValidForProgramming(link),
      });

    return {
      isLoading: loading,
      hasAppliedStructure,
      programmingState: resolveProgrammingGroupState(groupLabels),
      isAssignationLocked,
      canEditProgramming,
      isProgrammingDisabled: !!programmingTabBlockReason,
      programmingTooltipKey: programmingTabBlockReason
        ? PROGRAMMING_TAB_TOOLTIP_KEYS[programmingTabBlockReason]
        : null,
    };
  }, [linkId, data, loading, thresholdData, user?.abilities]);
};
